import React, { useState } from 'react';
import './StarRating.css';

interface StarRatingProps {
  // Note courante : entier 1-5 en mode interactif, peut etre fractionnaire (ex. 3.8) en lecture seule.
  value: number | null;
  max?: number;
  // true => affichage d'une moyenne, non cliquable (remplissage fractionnaire).
  readOnly?: boolean;
  // true => skeleton de chargement (etoiles grises qui pulsent).
  loading?: boolean;
  onRate?: (note: number) => void;
  ariaLabel?: string;
}

const STAR_PATH =
  'M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.169L12 18.896l-7.335 3.871 1.401-8.169L.132 9.211l8.2-1.193z';

const StarGlyph: React.FC<{ filled?: boolean }> = ({ filled }) => (
  <svg
    className={`star-rating-glyph${filled ? ' is-filled' : ''}`}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path d={STAR_PATH} />
  </svg>
);

/**
 * Notation par etoiles, trois modes :
 * - loading : skeleton (etoiles grises qui pulsent), non interactif.
 * - lecture seule : remplissage fractionnaire pour afficher une moyenne.
 * - interactif : 5 boutons cliquables (hover/focus en preview), onRate(note) au clic.
 * Etoile pleine = #f97316 (palette), vide = gris.
 */
const StarRating: React.FC<StarRatingProps> = ({ value, max = 5, readOnly = false, loading = false, onRate, ariaLabel }) => {
  const [hover, setHover] = useState<number | null>(null);
  const stars = Array.from({ length: max }, (_, i) => i + 1);

  if (loading) {
    return (
      <span className="star-rating star-rating--skeleton" aria-hidden="true">
        {stars.map((s) => (
          <StarGlyph key={s} />
        ))}
      </span>
    );
  }

  if (readOnly) {
    const borne = value != null && value > 0 ? Math.min(value, max) : 0;
    const ratio = borne / max;
    return (
      <span
        className="star-rating star-rating--readonly"
        role="img"
        aria-label={ariaLabel ?? `Note moyenne ${borne} sur ${max}`}
      >
        <span className="star-rating-track">
          {stars.map((s) => (
            <StarGlyph key={s} />
          ))}
          <span className="star-rating-fill" style={{ width: `${ratio * 100}%` }}>
            {stars.map((s) => (
              <StarGlyph key={s} filled />
            ))}
          </span>
        </span>
      </span>
    );
  }

  const active = hover ?? value ?? 0;
  return (
    <span className="star-rating star-rating--interactive" role="radiogroup" aria-label={ariaLabel ?? 'Noter'}>
      {stars.map((s) => (
        <button
          key={s}
          type="button"
          className="star-rating-btn"
          aria-label={`${s} étoile${s > 1 ? 's' : ''}`}
          aria-pressed={value === s}
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(null)}
          onFocus={() => setHover(s)}
          onBlur={() => setHover(null)}
          onClick={() => onRate?.(s)}
        >
          <StarGlyph filled={s <= active} />
        </button>
      ))}
    </span>
  );
};

export default StarRating;
